import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { ToastProvider } from "./components/ToastProvider";
import { Provider } from "react-redux";
import { store } from "./store/store";

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    {/* @ts-ignore react-redux Provider typing mismatch with current React setup */}
    <Provider store={store}>
      <BrowserRouter>
        <div className="app-scroll-root">
          {/* TODO(ts-migrate): remove ignore after ToastProvider is converted to TS */}
          {/* @ts-ignore */}
          <ToastProvider>
            <App />
          </ToastProvider>
        </div>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);

function syncAppScrollbarWidth() {
  const appScrollRoot = document.querySelector(".app-scroll-root");
  const scrollbarWidth =
    appScrollRoot instanceof HTMLElement ? Math.max(0, appScrollRoot.offsetWidth - appScrollRoot.clientWidth) : 0;
  document.documentElement.style.setProperty("--app-scrollbar-width", `${scrollbarWidth}px`);
}

const syncScrollbarOnNextFrame = () => {
  window.requestAnimationFrame(syncAppScrollbarWidth);
};

syncScrollbarOnNextFrame();
window.addEventListener("resize", syncScrollbarOnNextFrame);
window.addEventListener("load", syncScrollbarOnNextFrame);

const appScrollRoot = document.querySelector(".app-scroll-root");
if (appScrollRoot instanceof HTMLElement && "ResizeObserver" in window) {
  const resizeObserver = new ResizeObserver(() => {
    syncScrollbarOnNextFrame();
  });
  resizeObserver.observe(appScrollRoot);
}

if (appScrollRoot instanceof HTMLElement) {
  appScrollRoot.addEventListener("scroll", syncScrollbarOnNextFrame, { passive: true });

  if ("MutationObserver" in window) {
    const mutationObserver = new MutationObserver(() => {
      syncScrollbarOnNextFrame();
    });
    mutationObserver.observe(appScrollRoot, {
      childList: true,
      subtree: true,
    });
  }
}

reportWebVitals();

const isProd = process.env.NODE_ENV === "production";

async function clearServiceWorkersAndCachesInDev() {
  if (!("serviceWorker" in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (error) {
    console.warn("Failed to unregister service workers in dev:", error);
  }

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (error) {
      console.warn("Failed to clear caches in dev:", error);
    }
  }
}

// Service worker strategy:
// - development: aggressively clear stale SW/caches
// - production: register and force update activation
if ("serviceWorker" in navigator) {
  if (!isProd) {
    clearServiceWorkersAndCachesInDev();
  } else {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          const activateWaitingWorker = () => {
            if (!registration.waiting) return;
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          };

          if (registration.waiting) {
            activateWaitingWorker();
          }

          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                activateWaitingWorker();
              }
            });
          });

          navigator.serviceWorker.addEventListener("controllerchange", () => {
            window.location.reload();
          });
        })
        .catch((error) => {
          console.log("Service Worker registration failed:", error);
        });
    });
  }
}
